import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCalendarioDetalle, fetchCalendarioItems } from "../api/calendarioApi";
import { mapItems } from "../domain/calendarioMapItems";
import { sameYMD, ymd } from "../domain/calendarioDates";
import { getModalTheme } from "../domain/calendarioTheme";

export function useCalendario() {
    const [openSubtab, setOpenSubtab] = useState({});
    const [items, setItems] = useState([]);
    const [selectedDate, setSelectedDate] = useState(() => ymd(new Date()));
    const [isNarrow, setIsNarrow] = useState(
        typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
    );

    const calendarRef = useRef(null);
    const wrapRef = useRef(null);
    const tabsRef = useRef(null);

    const [selectedItem, setSelectedItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState("Detalles");

    // Datos calendario
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await fetchCalendarioItems();
                if (!alive) return;
                setItems(mapItems(data));
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Breakpoint
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 768px)");
        const handler = (e) => setIsNarrow(e.matches);
        mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
        return () => {
            mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
        };
    }, []);

    // Reset tab al abrir item
    useEffect(() => {
        if (selectedItem) setActiveTab("Detalles");
    }, [selectedItem]);

    // Asegurar scroll del tab visible
    useEffect(() => {
        if (!showModal || !tabsRef.current) return;
        const btns = tabsRef.current.querySelectorAll('button[data-tab]');
        const target = Array.from(btns).find((b) => b.getAttribute("data-tab") === activeTab);
        target?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, [activeTab, showModal]);

    // Recalcular tamaños
    useEffect(() => {
        const id = requestAnimationFrame(() => calendarRef.current?.getApi()?.updateSize());
        return () => cancelAnimationFrame(id);
    }, [items, isNarrow, selectedDate]);

    const modalTheme = useMemo(() => getModalTheme(selectedItem), [selectedItem]);

    const agendaDelDia = useMemo(() => {
        const s = selectedDate;
        return items
            .filter((ev) => {
                const start = new Date(ev.start);
                const end = ev.end ? new Date(ev.end) : start;

                const sDate = new Date(s); sDate.setHours(0, 0, 0, 0);
                const start0 = new Date(start); start0.setHours(0, 0, 0, 0);
                const end0 = new Date(end); end0.setHours(0, 0, 0, 0);

                return sDate >= start0 && sDate < end0;
            })
            .sort((a, b) => a.title.localeCompare(b.title));
    }, [items, selectedDate]);

    const participacionesOrdenadas = useMemo(() => {
        const parts = selectedItem?.data?.participaciones || [];
        if (parts.some((p) => p?.orden != null)) {
            return [...parts].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
        }
        return parts;
    }, [selectedItem]);

    const openDetalle = async ({ tipo, itemId }) => {
        const data = await fetchCalendarioDetalle(tipo, itemId);
        setSelectedItem({ tipo: String(tipo).toUpperCase(), data });
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    const onEventClick = async (info) => {
        const { id } = info.event;
        const [tipo, itemId] = String(id).split("-");
        try {
            await openDetalle({ tipo, itemId });
        } catch (e) {
            console.error(e);
            alert("No se pudo cargar el detalle.");
        }
    };

    const onDateClick = (info) => {
        const api = info.view.calendar;

        if (info.view.type === "multiMonthYear") {
            api.changeView("dayGridMonth", info.date);
            setSelectedDate(ymd(info.date));
            return;
        }
        setSelectedDate(ymd(info.date));
    };

    const onDatesSet = (info) => {
        const start = info.start;
        if (
            !sameYMD(selectedDate, start) &&
            new Date(selectedDate).getMonth() !== new Date(start).getMonth()
        ) {
            setSelectedDate(ymd(start));
        }
    };

    // Sustituye dayCellDidMount: devuelve clases según estado
    const dayCellClassNames = (arg) => (sameYMD(arg.date, selectedDate) ? ["is-selected"] : []);

    return {
        // refs
        calendarRef,
        wrapRef,
        tabsRef,

        // state
        openSubtab,
        setOpenSubtab,
        items,
        selectedDate,
        setSelectedDate,
        isNarrow,

        selectedItem,
        showModal,
        closeModal,
        activeTab,
        setActiveTab,
        modalTheme,

        agendaDelDia,
        participacionesOrdenadas,

        // handlers
        openDetalle,
        onEventClick,
        onDateClick,
        onDatesSet,
        dayCellClassNames,
    };
}
